import React, { useState } from "react";
import axios from "axios";
import { useLang } from "@/i18n/LanguageContext";
import Reveal from "@/components/site/Reveal";
import { RevealTextInstant } from "@/components/site/RevealText";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EASE = [0.76, 0, 0.24, 1];

export default function ContactPage() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", project_description: "" });
  const [file, setFile] = useState(null);
  const [state, setState] = useState({ status: "idle", error: "" });

  const MAX_MB = 20;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) { setFile(null); return; }
    if (picked.size > MAX_MB * 1024 * 1024) {
      setState({ status: "error", error: `File > ${MAX_MB} MB` });
      e.target.value = "";
      return;
    }
    setFile(picked);
    setState({ status: "idle", error: "" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ status: "submitting", error: "" });
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("company", form.company || "");
      fd.append("email", form.email);
      fd.append("project_description", form.project_description);
      fd.append("language", lang);
      if (file) fd.append("file", file, file.name);

      await axios.post(`${API}/contact`, fd);
      setState({ status: "success", error: "" });
      setForm({ name: "", company: "", email: "", project_description: "" });
      setFile(null);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = t.contact.error;
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw)) msg = raw.map(x => x?.msg || "").filter(Boolean).join(" ") || t.contact.error;
      setState({ status: "error", error: msg });
    }
  };

  return (
    <div data-testid="page-contact" className="pt-32 md:pt-40">
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 pb-16 md:pb-24">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-3 az-tag"><span className="az-tag__dot" /><span>{t.contact.kicker}</span></div>
          <div className="col-span-12 md:col-span-3 md:col-start-10 md:text-right font-mono text-[10px] tracking-widest uppercase text-[#86868B]">
            IT / EN
          </div>
        </div>
        <RevealTextInstant
          as="h1"
          lines={[t.contact.title]}
          className="font-serif tracking-tighter leading-[0.94] text-[11vw] md:text-[6vw] lg:text-[5.75rem]"
          lineClassName="block"
        />
        <div className="mt-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <Reveal delay={0.15}>
              <p className="font-sans text-lg md:text-xl leading-relaxed text-[#86868B] max-w-[52ch]">
                {t.contact.lead}
              </p>
              <div className="mt-8 flex flex-col gap-1">
                <span className="font-mono text-[10px] tracking-widest uppercase text-[#86868B]">{t.contact.directEmailLabel}</span>
                <a href={`mailto:${t.common.email}`} className="font-serif text-2xl md:text-3xl az-link">
                  {t.common.email}
                </a>
              </div>
              <div className="mt-6 flex flex-col gap-1">
                <span className="font-mono text-[10px] tracking-widest uppercase text-[#86868B]">{t.common.phoneLabel}</span>
                <a data-testid="contact-phone" href={`tel:${t.common.phone.replace(/[^+\d]/g, "")}`} className="font-serif text-2xl md:text-3xl az-link">
                  {t.common.phone}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-[#E5E5EA] py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="font-mono text-[10px] tracking-widest uppercase text-[#0A0A0A] mb-6">Studio · Antonio Zaccaria</div>
            <p className="font-serif text-3xl md:text-4xl leading-tight tracking-tight max-w-[18ch]">
              {t.common.role}.
            </p>
            <p className="mt-6 font-mono text-[10px] tracking-widest uppercase text-[#86868B]">
              {t.common.based}
            </p>
          </div>

          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8">
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">
                  {t.contact.name} <span className="text-[#0055FF]">— {t.contact.requiredMark}</span>
                </label>
                <input
                  type="text"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={onChange("name")}
                  className="az-input"
                  data-testid="contact-input-name"
                />
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">
                  {t.contact.company}
                </label>
                <input
                  type="text"
                  value={form.company}
                  onChange={onChange("company")}
                  className="az-input"
                  data-testid="contact-input-company"
                />
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">
                  {t.contact.email} <span className="text-[#0055FF]">— {t.contact.requiredMark}</span>
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  className="az-input"
                  data-testid="contact-input-email"
                />
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">
                  {t.contact.description} <span className="text-[#0055FF]">— {t.contact.requiredMark}</span>
                </label>
                <textarea
                  required
                  minLength={10}
                  rows={5}
                  value={form.project_description}
                  onChange={onChange("project_description")}
                  className="az-input az-input--textarea"
                  placeholder={t.contact.descriptionHint}
                  data-testid="contact-input-description"
                />
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">
                  {t.contact.file}
                </label>
                <label className="flex items-center justify-between border-b border-[#E5E5EA] py-4 cursor-pointer group">
                  <span className="font-sans text-[15px] text-[#0A0A0A] truncate max-w-[70%]">
                    {file ? `${file.name} · ${(file.size / (1024 * 1024)).toFixed(2)} MB` : t.contact.fileHint}
                  </span>
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#0055FF] group-hover:translate-x-1 transition-transform duration-300">
                    Browse →
                  </span>
                  <input type="file" onChange={onFile} className="hidden" data-testid="contact-input-file" />
                </label>
              </div>

              <div className="pt-4 flex items-center gap-6 flex-wrap">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={state.status === "submitting"}
                  className="az-btn"
                >
                  <span>{state.status === "submitting" ? t.contact.submitting : t.contact.submit}</span>
                  <span className="az-btn__arrow" aria-hidden="true">→</span>
                </button>

                <AnimatePresence>
                  {state.status === "success" && (
                    <motion.span
                      key="ok"
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      data-testid="contact-success"
                      className="font-mono text-[11px] tracking-widest uppercase text-[#0055FF]"
                    >
                      ✓ {t.contact.success}
                    </motion.span>
                  )}
                  {state.status === "error" && (
                    <motion.span
                      key="err"
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      data-testid="contact-error"
                      className="font-mono text-[11px] tracking-widest uppercase text-red-600"
                    >
                      ✕ {state.error || t.contact.error}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
